import { formatDateTime } from '../utils/dateFormatter'
import { apiService } from './api.service'

export type WorkflowHistoryEntryType = 'status_change' | 'claim' | 'unclaim'

export interface WorkflowHistoryEntry {
  entry_type: WorkflowHistoryEntryType
  performed_at: string
  performed_by_username: string | null
  from_status_id: number | null
  from_status_name: string | null
  to_status_id: number | null
  to_status_name: string | null
  workflow_definition_id: number | null
  workflow_definition_name: string | null
  assigned_user_ids: number[]
  assigned_group_ids: number[]
  comment: string
}

class DocumentWorkflowHistoryService {
  async getWorkflowHistory(documentId: number): Promise<WorkflowHistoryEntry[]> {
    const response = await apiService.get<WorkflowHistoryEntry[]>(
      `documents/${documentId}/workflow-history/`
    )
    return response.data
  }

  /**
   * Status-change entries that moved the document into the given workflow status (to_status_id).
   * Sorted by performed_at descending (most recent first).
   */
  getEntriesForStep(history: WorkflowHistoryEntry[], workflowStatusId: number): WorkflowHistoryEntry[] {
    return history
      .filter((e) => e.entry_type === 'status_change' && e.to_status_id === workflowStatusId)
      .sort((a, b) => new Date(b.performed_at).getTime() - new Date(a.performed_at).getTime())
  }

  /**
   * Latest entry that moved the document into this step (for "who & when" and detail).
   */
  getLatestEntryForStep(history: WorkflowHistoryEntry[], workflowStatusId: number): WorkflowHistoryEntry | null {
    const entries = this.getEntriesForStep(history, workflowStatusId)
    return entries[0] ?? null
  }

  getLatestWorkflowComment(history: WorkflowHistoryEntry[]): string {
    const latest = history
      .filter((e) => e.entry_type === 'status_change')
      .sort((a, b) => new Date(b.performed_at).getTime() - new Date(a.performed_at).getTime())[0]
    return latest?.comment?.trim() ?? ''
  }

  formatAuditDate(iso: string): string {
    return formatDateTime(iso)
  }

  /**
   * Human-readable action label for a status_change entry (e.g. "Draft → Pending" or "Started workflow").
   */
  getActionLabel(entry: WorkflowHistoryEntry): string {
    if (entry.entry_type !== 'status_change') return ''
    const from = entry.from_status_name ?? ''
    const to = entry.to_status_name ?? ''
    if (!from.trim()) return 'Started workflow'
    return `${from} → ${to}`
  }
}

export const documentWorkflowHistoryService = new DocumentWorkflowHistoryService()
